import { createContext, useCallback, useMemo, useState, type PropsWithChildren } from 'react';
import type { AdminSession } from '@/types/auth';

type AuthContextValue = {
  session: AdminSession | null;
  signIn: (email: string, password: string) => Promise<boolean>;
  signOut: () => void;
};

export const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: PropsWithChildren) {
  const [session, setSession] = useState<AdminSession | null>(() => {
    const raw = window.localStorage.getItem('divergente.admin.session');
    return raw ? (JSON.parse(raw) as AdminSession) : null;
  });

  const signIn = useCallback(async (email: string, password: string) => {
    const accepted = email.trim().toLowerCase() === 'admin@eldivergente.ar' && password.length >= 8;
    if (!accepted) return false;

    const nextSession: AdminSession = {
      userId: 'admin-local-bootstrap',
      email,
      role: 'admin',
      createdAt: new Date().toISOString()
    };
    window.localStorage.setItem('divergente.admin.session', JSON.stringify(nextSession));
    setSession(nextSession);
    return true;
  }, []);

  const signOut = useCallback(() => {
    window.localStorage.removeItem('divergente.admin.session');
    setSession(null);
  }, []);

  const value = useMemo(() => ({ session, signIn, signOut }), [session, signIn, signOut]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
